import React, { useEffect, useState } from "react";
import { fetchMe, logout, startGoogleLogin } from "../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /profile — signed-in user's dashboard:
 *  • Google identity + Sign out
 *  • Token wallet (balance + each code with remaining tokens)
 *  • Purchase history (Gumroad sales)
 *  • Consultation history (Gumroad sales for the $3 consult product)
 *  • Course enrollments + exchange spends (activity)
 */
export default function ProfilePage() {
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const me = await fetchMe();
    if (!me) {
      setLoading(false);
      return;
    }
    setUser(me);
    const [w, p] = await Promise.all([
      fetch(`${API}/api/auth/tokens`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
      fetch(`${API}/api/auth/purchases`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
    ]);
    setWallet(w);
    setPurchases(p);
    setLoading(false);
  };

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    reload();
  }, []);

  if (loading) {
    return (
      <ProfileShell>
        <p style={muted}>Loading your profile…</p>
      </ProfileShell>
    );
  }

  if (!user) {
    return (
      <ProfileShell title="Profile">
        <p style={muted}>Sign in with Google to see your token wallet, purchases, and consultation history.</p>
        <button
          type="button"
          data-testid="profile-signin-btn"
          onClick={() => startGoogleLogin("/profile")}
          style={pill("solid")}
        >
          Sign in with Google
        </button>
      </ProfileShell>
    );
  }

  const balance = wallet?.balance ?? 0;

  return (
    <ProfileShell title="Your Federation Profile">
      {/* Identity */}
      <section data-testid="profile-identity" style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          {user.picture ? (
            <img
              src={user.picture}
              alt=""
              width={72}
              height={72}
              style={{ borderRadius: "50%", border: "2px solid gold", objectFit: "cover" }}
            />
          ) : null}
          <div style={{ flex: 1 }}>
            <h2 style={h2} data-testid="profile-user-name">{user.name || user.email}</h2>
            <p style={{ ...muted, margin: 0 }} data-testid="profile-user-email">{user.email}</p>
          </div>
          <button
            type="button"
            data-testid="profile-signout-btn"
            onClick={async () => {
              await logout();
              window.location.href = "/university";
            }}
            style={pill("outline")}
          >
            Sign Out
          </button>
        </div>
      </section>

      {/* Wallet */}
      <section data-testid="profile-wallet" style={card}>
        <h2 style={h2}>Token Wallet</h2>
        <p style={{ ...muted, marginTop: 0 }}>
          <strong data-testid="profile-balance" style={{ color: "#0fc", fontSize: "1.6rem" }}>
            {balance}
          </strong>{" "}
          token{balance === 1 ? "" : "s"} available across{" "}
          {(wallet?.codes || []).length} code{(wallet?.codes || []).length === 1 ? "" : "s"}.
        </p>
        {(wallet?.codes || []).length === 0 ? (
          <p style={muted}>
            No codes yet.{" "}
            <a href="https://next-xus.com" target="_blank" rel="noopener noreferrer" style={link}>
              Get a $5 Access Token
            </a>{" "}
            or paste a code you already own on any protected page.
          </p>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Code</th>
                <th style={th}>Remaining / Total</th>
                <th style={th}>Status</th>
                <th style={th}>Source</th>
              </tr>
            </thead>
            <tbody>
              {(wallet?.codes || []).map((c) => (
                <tr key={c.code} data-testid={`profile-code-row-${c.code}`}>
                  <td style={{ ...td, fontFamily: "ui-monospace, Menlo, monospace" }}>{c.code}</td>
                  <td style={td}>
                    <strong style={{ color: "#0fc" }}>{c.tokens_remaining}</strong> / {c.token_value}
                  </td>
                  <td style={td}>{c.status}</td>
                  <td style={td}>{c.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Purchases */}
      <section data-testid="profile-purchases" style={card}>
        <h2 style={h2}>Purchase History</h2>
        {(purchases?.sales || []).length === 0 ? (
          <p style={muted}>No purchases yet.</p>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={th}>Product</th>
                <th style={th}>Amount</th>
                <th style={th}>Code Minted</th>
              </tr>
            </thead>
            <tbody>
              {(purchases?.sales || []).map((s) => (
                <tr key={s.sale_id} data-testid={`profile-sale-${s.sale_id}`}>
                  <td style={td}>{formatDate(s.received_at)}</td>
                  <td style={td}>{s.product_name || s.product_slug}</td>
                  <td style={td}>
                    ${Number(s.amount || 0).toFixed(2)} {(s.currency || "usd").toUpperCase()}
                  </td>
                  <td style={{ ...td, fontFamily: "ui-monospace, Menlo, monospace" }}>
                    {s.redemption_code || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Consultation history */}
      <section data-testid="profile-consultations" style={card}>
        <h2 style={h2}>Consultation History</h2>
        {(purchases?.consultations || []).length === 0 ? (
          <p style={muted}>No consultations booked yet.</p>
        ) : (
          <ul style={{ paddingLeft: "1.2rem", color: "#e0e0e0", fontFamily: "Georgia, serif", lineHeight: 1.7 }}>
            {purchases.consultations.map((c) => (
              <li key={c.sale_id} data-testid={`profile-consult-${c.sale_id}`}>
                {formatDate(c.received_at)} · ${Number(c.amount || 0).toFixed(2)} {(c.currency || "usd").toUpperCase()}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Activity */}
      {(purchases?.enrollments || []).length > 0 || (purchases?.exchange_spends || []).length > 0 ? (
        <section data-testid="profile-activity" style={card}>
          <h2 style={h2}>Activity</h2>
          {(purchases?.enrollments || []).length > 0 ? (
            <>
              <h3 style={h3}>Course Enrollments</h3>
              <ul style={list}>
                {purchases.enrollments.map((e, i) => (
                  <li key={`${e.course_id}-${i}`} data-testid={`profile-enroll-${e.course_id}`}>
                    {formatDate(e.enrolled_at)} · <strong>{e.course_title || e.course_id}</strong>{" "}
                    · {e.tokens_spent ?? 0} tokens
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {(purchases?.exchange_spends || []).length > 0 ? (
            <>
              <h3 style={h3}>Exchange Spends</h3>
              <ul style={list}>
                {purchases.exchange_spends.map((p, i) => (
                  <li key={`${p.offering_id}-${i}`} data-testid={`profile-spend-${p.offering_id}`}>
                    {formatDate(p.created_at || p.purchased_at)} ·{" "}
                    <strong>{p.offering_title || p.offering_id}</strong> · {p.tokens_spent} tokens
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      ) : null}
    </ProfileShell>
  );
}

function ProfileShell({ title = "Profile", children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a0a0f",
        color: "#e0e0e0",
        fontFamily: "Georgia, serif",
        padding: "5rem 1.25rem 4rem",
      }}
    >
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <h1
          style={{
            color: "gold",
            fontFamily: "Georgia, serif",
            fontSize: "2.4rem",
            letterSpacing: ".08em",
            margin: "0 0 1.5rem",
          }}
        >
          {title}
        </h1>
        {children}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

const card = {
  border: "1px solid gold",
  borderRadius: 12,
  padding: "1.5rem 1.75rem",
  marginBottom: "1.5rem",
  background: "#111118",
};
const h2 = {
  color: "gold",
  fontFamily: "Georgia, serif",
  fontSize: "1.4rem",
  letterSpacing: ".05em",
  marginTop: 0,
};
const h3 = {
  color: "gold",
  fontFamily: "Georgia, serif",
  fontSize: "1.1rem",
  fontStyle: "italic",
  marginBottom: ".4rem",
};
const muted = { color: "#94a3b8", fontFamily: "Georgia, serif", fontSize: 15 };
const link = { color: "gold" };
const table = { width: "100%", borderCollapse: "collapse", fontFamily: "Georgia, serif" };
const th = {
  textAlign: "left",
  color: "gold",
  padding: ".6rem .5rem",
  borderBottom: "1px solid rgba(255,215,0,.35)",
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: ".08em",
};
const td = {
  padding: ".55rem .5rem",
  borderBottom: "1px dotted rgba(255,215,0,.15)",
  color: "#e0e0e0",
  fontSize: 14,
};
const list = { paddingLeft: "1.2rem", color: "#e0e0e0", lineHeight: 1.7 };
function pill(variant) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 44,
    padding: "0 1.5rem",
    borderRadius: 999,
    fontFamily: "Georgia, serif",
    fontSize: 14,
    letterSpacing: ".12em",
    textTransform: "uppercase",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "1px solid gold",
  };
  if (variant === "solid") return { ...base, background: "gold", color: "#0a0a0f" };
  return { ...base, background: "transparent", color: "gold" };
}
